import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import useShouldRefresh from '@/hooks/useShouldRefresh';
import { installedProductsKeys } from '@/solaris-lens-common-lib/src/constants/queryKeys/InstalledProducts';
import {
  getTurbineAlarmEventsServ,
  getTurbineAlarmSubscriptionsServ,
  postTurbineAlarmSubscriptionsServ,
  updateTurbineAlarmSubscriptionsOperationServ
} from '@/solaris-lens-common-lib/src/services/notifications';
import { RootState } from '@/store';

interface UseGetTurbineAlarmEventsProps {
  useQueryOptions?: Record<string, unknown>;
  eventCode: string;
  bundleAppId: string;
}

export const useGetTurbineAlarmEvents = ({
  useQueryOptions = {},
  eventCode = '',
  bundleAppId = ''
}: UseGetTurbineAlarmEventsProps) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: installedProductsKeys.listByFilters(
      {
        eventCode,
        bundleAppId,
        type: 'turbineAlarmEvents'
      },
      token
    ),
    queryFn: async () => {
      if (!token) {
        throw new Error('No access token found');
      }
      const res = await getTurbineAlarmEventsServ(
        token,
        eventCode,
        bundleAppId
      );
      return res.data;
    },
    enabled: Boolean(token && eventCode && bundleAppId),
    ...useQueryOptions
  });
};

interface UseGetTurbineAlarmSubscriptionsProps {
  useQueryOptions?: Record<string, unknown>;
  eventCode: string | string[];
  bundleAppId: string;
  eventName?: string;
}

export const useGetTurbineAlarmSubscriptions = ({
  useQueryOptions = {},
  eventCode = '',
  bundleAppId = '',
  eventName = ''
}: UseGetTurbineAlarmSubscriptionsProps) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery({
    queryKey: installedProductsKeys.listByFilters(
      {
        eventCode,
        eventName,
        bundleAppId,
        type: 'turbineAlarmSubscriptions'
      },
      token
    ),
    queryFn: async () => {
      if (!token) {
        throw new Error('No access token found');
      }
      const res = await getTurbineAlarmSubscriptionsServ(
        token,
        eventCode,
        bundleAppId,
        eventName
      );
      return res.data;
    },
    enabled: Boolean(token && eventCode && bundleAppId),
    ...useQueryOptions
  });
};

export const usePostTurbineAlarmSubscription = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();
  useShouldRefresh();

  return useMutation({
    mutationFn: async ({
      bundleAppId,
      eventCode,
      eventName
    }: {
      bundleAppId: string;
      eventCode: string;
      eventName: string;
    }) => {
      if (!token) {
        throw new Error('No access token found');
      }
      const res = await postTurbineAlarmSubscriptionsServ(
        token,
        bundleAppId,
        eventCode,
        eventName
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: installedProductsKeys.all
      });
    }
  });
};

export const useDeleteTurbineAlarmSubscription = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const queryClient = useQueryClient();
  useShouldRefresh();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      if (!token) {
        throw new Error('No access token found');
      }
      const res = await updateTurbineAlarmSubscriptionsOperationServ(token, id);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: installedProductsKeys.all
      });
    }
  });
};
